import { useCallback, useMemo } from 'react';
import { useViewRulesStore } from '../stores/viewRulesStore';
import type { ViewRule } from '../models/ViewRules';
import type { LensPresetChip } from '../components/LensPresetSection';

/**
 * Declarative field-pack lens preset: a one-click `colorBy` / `heatmap`
 * rule on a real attribute. Toggling adds/removes the rule in the shared
 * viewRulesStore; active state is derived by matching existing rules.
 *
 * This is the logic shared by NetworkLensPresets / ProcessLensPresets /
 * OrgChartLensPresets — domains only declare the array.
 */
export interface FieldPackLensPreset {
  id: string;
  label: string;
  icon: React.ElementType;
  kind: 'colorBy' | 'heatmap';
  field: string;
  description?: string;
  minColor?: string;
  maxColor?: string;
}

/**
 * A computed lens needs per-node compute (graph traversal / lookup) that
 * stamps a synthetic attribute before applying a view rule. The compute +
 * teardown live in the domain (e.g. scenarios/computedLenses); this hook
 * just wires the chip toggle to that adapter.
 */
export interface ComputedLensPreset<TId extends string = string> {
  id: TId;
  label: string;
  icon: React.ElementType;
  description?: string;
}

export interface ComputedLensAdapter<TId extends string = string> {
  apply: (id: TId) => void;
  clear: (id: TId) => void;
  isActive: (id: TId, rules: ViewRule[]) => boolean;
}

interface FieldPackResult {
  chips: LensPresetChip[];
  activeCount: number;
  clearAll: () => void;
}

/** Build toggleable chips for field-pack presets wired to viewRulesStore. */
export function useFieldPackLensPresets(presets: readonly FieldPackLensPreset[]): FieldPackResult {
  const rules = useViewRulesStore((s) => s.rules);
  const addViewRule = useViewRulesStore((s) => s.addViewRule);
  const removeRule = useViewRulesStore((s) => s.removeRule);

  // presetId → matching enabled ruleId (active when present)
  const activeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const preset of presets) {
      const match = rules.find((r) => {
        if (!r.enabled) return false;
        return preset.kind === 'colorBy'
          ? (r.display?.colorByField?.field === preset.field || (preset.field === 'flightRisk' && r.display?.heatmap?.field === preset.field))
          : r.display?.heatmap?.field === preset.field;
      });
      if (match) map.set(preset.id, match.id);
    }
    return map;
  }, [presets, rules]);

  const toggle = useCallback(
    (preset: FieldPackLensPreset) => {
      const existing = activeMap.get(preset.id);
      if (existing) {
        removeRule(existing);
        return;
      }
      addViewRule(preset.kind, {
        field: preset.field,
        minColor: preset.minColor,
        maxColor: preset.maxColor,
      });
    },
    [activeMap, addViewRule, removeRule],
  );

  const chips = useMemo<LensPresetChip[]>(
    () =>
      presets.map((preset) => ({
        id: preset.id,
        label: preset.label,
        icon: preset.icon,
        description: preset.description,
        isActive: activeMap.has(preset.id),
        onToggle: () => toggle(preset),
      })),
    [presets, activeMap, toggle],
  );

  const clearAll = useCallback(() => {
    for (const ruleId of activeMap.values()) removeRule(ruleId);
  }, [activeMap, removeRule]);

  return { chips, activeCount: activeMap.size, clearAll };
}

interface ComputedResult {
  chips: LensPresetChip[];
  activeCount: number;
  clearAll: () => void;
}

/** Build toggleable chips for computed presets wired to a domain adapter. */
export function useComputedLensPresets<TId extends string>(
  presets: readonly ComputedLensPreset<TId>[],
  adapter: ComputedLensAdapter<TId>,
): ComputedResult {
  const rules = useViewRulesStore((s) => s.rules);

  const activeSet = useMemo(() => {
    const set = new Set<TId>();
    for (const preset of presets) {
      if (adapter.isActive(preset.id, rules)) set.add(preset.id);
    }
    return set;
  }, [presets, adapter, rules]);

  const chips = useMemo<LensPresetChip[]>(
    () =>
      presets.map((preset) => ({
        id: preset.id,
        label: preset.label,
        icon: preset.icon,
        description: preset.description,
        isActive: activeSet.has(preset.id),
        onToggle: () =>
          activeSet.has(preset.id) ? adapter.clear(preset.id) : adapter.apply(preset.id),
      })),
    [presets, activeSet, adapter],
  );

  const clearAll = useCallback(() => {
    for (const id of activeSet) adapter.clear(id);
  }, [activeSet, adapter]);

  return { chips, activeCount: activeSet.size, clearAll };
}
